import {Tooltip} from '@mui/material'
import {Box, Loading, Skeleton, Typography} from '@viptech/react-components'
import {useEffect, useRef, useState} from 'react'
import ReactApexChart from 'react-apexcharts'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'
import {abbreviateNumber} from 'src/utilities/formatter'
import {calculatePercent} from '../default-dashboard/functions'

type DonutInfoProps = {
  title: string
  loading: boolean
  total?: number
  data: ApexCharts.ApexOptions['series']
  labelsXaxis?: Array<string>
  colors?: Array<string>
  refetching?: boolean
  labelOnLegend: string
}

const GAP = 52

export default function DashboardDonutChartCard({title, loading, refetching, total, data, labelsXaxis, colors, labelOnLegend}: DonutInfoProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const {styledTheme} = usePersonalizedThemeByCompany()
  const [containerWidth, setContainerWidth] = useState(0)
  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth - GAP)
      }
    }

    updateContainerWidth()

    window.addEventListener('resize', updateContainerWidth)
    const observer = new ResizeObserver(updateContainerWidth)
    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => {
      window.removeEventListener('resize', updateContainerWidth)
      if (containerRef.current) {
        observer.unobserve(containerRef.current)
      }
    }
  }, [])

  const opts: ApexCharts.ApexOptions = {
    series: data ?? [],

    chart: {
      type: 'donut',
      fontFamily: 'Inter, sans-serif',
    },
    states: {
      hover: {
        filter: {
          type: 'none',
        },
      },
      active: {
        filter: {
          type: 'none',
        },
      },
    },
    dataLabels: {
      enabled: false,
      dropShadow: {
        enabled: true,
      },
    },
    tooltip: {
      fillSeriesColor: false,
    },
    plotOptions: {
      pie: {
        customScale: 1,
        donut: {
          size: '70%',
        },
      },
    },
    yaxis: {
      show: true,
      labels: {
        formatter: function (val) {
          return `${val.toFixed(0)} ${labelOnLegend} (${calculatePercent(val, total).toFixed(0)}%)`
        },
      },
      decimalsInFloat: 0,
    },
    noData: {
      text: 'Nenhum dado foi encontrado',
      align: 'center',
      verticalAlign: 'middle',
      style: {
        color: '#000000',
        fontSize: '12px',
        fontFamily: "'Inter', 'sans-serif",
      },
    },
    colors: colors ? colors : ['#BA281E', '#02C7C1', '#26BA02', '#F2B252', '#0085FF', '#653783', '#7A2B00', '#0D7A77', '#284561', '#F1F1F1', '#1D1D1D'],
    labels: labelsXaxis ?? [],
    legend: {
      show: true,
      position: 'bottom',
    },
  }

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth - GAP)
    }
  }, [loading, refetching])

  return (
    <Box
      ref={containerRef}
      backgroundColor='white'
      borderRadius='16px'
      width='100%'
      position='relative'
      height='320px'
      style={{boxShadow: '0px 5px 8px 0px #00000026', display: 'grid'}}>
      <Box display='flex' direction='column' padding='24px 32px' rowGap='6px' align='center'>
        <Typography variant='label' size='16px' marginTop='8px' color={styledTheme.dashboard.colors.title} weight={600}>
          {title}
        </Typography>
        {refetching && !loading && (
          <Tooltip title='Buscando atualizações' placement='top'>
            <Box position='absolute' right='24px' display='flex' columnGap='8px'>
              <Loading size='20px' />
            </Box>
          </Tooltip>
        )}
        {loading ? (
          <Box display='flex' flex={1} direction='column' width='100%' align='center' justify='center'>
            <Box marginTop='12px' position='relative'>
              <Skeleton borderRadius='100%' width={'172px'} height={'172px'} />
              <Box
                display='flex'
                align='center'
                justify='center'
                position='absolute'
                backgroundColor='white'
                borderRadius='100%'
                width={'116px'}
                height={'116px'}
                style={{top: '50%', left: '50%', transform: 'translate(-50%, -50%)'}}></Box>
            </Box>
            <Box display='flex' columnGap='8px' marginTop='8px' flexWrap='wrap' align='center' justify='center'>
              <Skeleton width={'96px'} height={'12px'} borderRadius='4px' />
              <Skeleton width={'96px'} height={'12px'} borderRadius='4px' />
              <Skeleton width={'96px'} height={'12px'} borderRadius='4px' />
            </Box>
          </Box>
        ) : (
          <Box position='relative'>
            <ReactApexChart options={opts} series={opts.series} type='donut' height={260} width={containerWidth} />
            {!!total && (
              <Tooltip title={total + ' faces'} placement='top'>
                <Box position='absolute' style={{top: '44%', left: '50%', transform: 'translate(-50%, -60%)'}}>
                  <Typography variant='label' weight={700} size='32px' color='#98A2B3'>
                    {abbreviateNumber(total)}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}
